<template>
  <LoaderFullPage v-if="userSettingsStore.loadingFetch" />
  <template v-else>
    <div class="card">
      <form
        method="post"
        class="user-settings"
        @submit.prevent="handleSubmitForm"
      >
        <template v-for="key in inputFieldNames" :key="key">
          <div class="field" :class="`field-${key}`">
            <label :for="key" class="label"
              >{{ inputFieldLabels[key] }} : *</label
            >
            <input :id="key" v-model="localData[key]" type="text" required />
            <span v-if="!isFormValid && !localData[key]" class="error"
              >{{ inputFieldLabels[key] }} est requis</span
            >
          </div>
        </template>

        <FormSelect
          v-model="localData.sector"
          label="Secteur"
          :items="userStore.thematics"
          item-label="name"
          item-value="_id"
          :error="
            !isFormValid && !localData.sector
              ? 'Veuillez renseigner votre secteur.'
              : null
          "
          required
        />

        <div class="field field-email">
          <label for="email" class="label">Email :</label>
          <input
            id="email"
            :value="userSettingsStore.user.email"
            disabled="true"
            type="email"
          />
          <div class="email-warning">
            Pour modifier votre adresse email, veuillez nous contacter via le
            chat.
          </div>
        </div>

        <CheckBox v-model:checked="localData.newsletter">
          Je veux recevoir
          <BaseLink href="https://mailchi.mp/saper-vedere/ijuklebci2">
            la newsletter
          </BaseLink>
        </CheckBox>

        <button
          v-focus
          class="btn-primary"
          :disabled="userSettingsStore.loadingUpdate"
          type="submit"
        >
          Enregistrer
        </button>
      </form>
    </div>
  </template>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import LoaderFullPage from "@follaw-fr/components/reusable/molecules/LoaderFullPage.vue";
import CheckBox from "@follaw-fr/components/reusable/atoms/CheckBox.vue";
import BaseLink from "@follaw-fr/components/general/atoms/BaseLink.vue";
import FormSelect from "@follaw-fr/components/reusable/molecules/FormSelect.vue";
import { useUserSettingsStore } from "@follaw-fr/stores/user-settings";
import { useUserStore } from "@follaw-fr/stores/user";

const toast = useToast();
const userSettingsStore = useUserSettingsStore();
const userStore = useUserStore();

const isFormValid = ref(true);

const inputFieldLabels = {
  first_name: "Prénom",
  last_name: "Nom de famille",
  occupation: "Fonction",
};

const inputFieldNames = Object.keys(inputFieldLabels);

// Ignore the warning because we are using a watch() just below
const localData = ref({});

function validate() {
  isFormValid.value = inputFieldNames.every(
    (key) => localData.value[key].trim() !== ""
  );

  return isFormValid.value;
}

// Compare old and new values and select only the changed values
function findChanges(original, updated) {
  const changes = {};
  Object.keys(updated).forEach((key) => {
    if (updated[key] !== original[key]) {
      changes[key] = updated[key];
    }
  });
  return changes;
}

// Submit the form with only the changed keys
const handleSubmitForm = () => {
  if (validate()) {
    const changes = findChanges(userSettingsStore.user, localData.value);
    if (Object.keys(changes).length > 0) {
      userSettingsStore.updateUserSettings(changes);
    } else {
      toast.info("Aucun changement à enregistrer");
    }
  }
};

onMounted(() => {
  localData.value = {
    ...Object.fromEntries(
      inputFieldNames.map((key) => [key, userSettingsStore.user[key]])
    ),
    sector: userSettingsStore.user.sector,
    newsletter: userSettingsStore.user.newsletter,
  };
});
</script>

<style lang="scss">
.user-settings {
  max-width: 700px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .label {
    font-weight: var(--fw-semibold);
    line-height: var(--lh-normal);
  }
  .newsletter-wrapper {
    padding: 18px 0;
  }
  .error {
    margin-left: 10px;
    color: var(--clr-e300);
    font-style: italic;
    font-size: 0.875em;
  }

  > button {
    align-self: flex-start;
  }

  input[type="text"],
  input[type="email"],
  .select-box,
  .menu {
    max-width: 250px;
  }

  .field-email {
    input#email {
      cursor: not-allowed;
    }

    &:hover {
      .email-warning {
        color: var(--clr-e300);
      }
    }
  }
}

p {
  margin: 0;
  line-height: var(--lh-normal);
}

.current-plan {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .plan-infos {
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    padding: 8px 16px;
    background-color: var(--clr-n2);

    .plan-icon {
      width: 22px;
      height: 22px;
    }

    .plan-end {
      margin-left: auto;

      .date {
        font-weight: var(--fw-semibold);
      }
    }
  }
}

.plan-compare {
  text-decoration: underline;
}

.to-stripe {
  align-self: flex-start;
}
</style>
